/**
 * The deck: what happens to a crown between councils.
 *
 * Every card here fires off state the strategic layer already keeps. Not one of
 * them rolls a die to decide *whether* it is eligible — the die decides only
 * which of the eligible cards is drawn. The gates read, in order of how much
 * work they do:
 *
 *   | Card                    | What it reads                                    |
 *   |-------------------------|--------------------------------------------------|
 *   | `succession`            | the turn, holdings, the treasury                 |
 *   | `bad_harvest`           | regions, holdings per region, region character   |
 *   | `host_will_not_march`   | `Army.idleTurns`, `supply`, `victories`          |
 *   | `plague_on_the_lane`    | trade control, node membership, node neighbours  |
 *   | `envoy_at_the_gate`     | opinion, war flags, relative strength            |
 *   | `envoy_answered`        | the answer given to the card above               |
 *   | `temple_asks`           | the Temple's standing, confession, holdings      |
 *   | `camp_sends_a_man`      | `BanditCamp` on ground this crown holds          |
 *   | `fourth_year`           | `War.startTurn`, `score`, `battlesLost`          |
 *   | `the_ledger`            | the treasury, holdings, the Counting Houses      |
 *   | `ground_remembered`     | `ProvinceState.claimedBy`                        |
 *   | `two_houses_one_ford`   | holdings, tiers, the Great Families              |
 *   | `unwalled_march`        | `fort`, the province graph, war and opinion      |
 *
 * ── The rule this project has learned twice ──────────────────────────────
 *
 * Two features have shipped whose seam fired zero times with every test green.
 * So every gate above was written against a **measured** board rather than an
 * imagined one: a hundred and forty months, six seeds, counting what the realm
 * actually produces. Three tempting gates were thrown away because of what came
 * back, and they are recorded here so nobody adds them again:
 *
 *   · **A fleet at sea.** 0.002 fleets are under sail in a given month across
 *     the whole realm. The AI builds hulls and then leaves them at anchor. An
 *     event about a voyage would fire about once per five hundred campaigns.
 *   · **A holding above the fourth rung.** Zero. Ever. In eight hundred and
 *     forty month-samples not one province on the board reached tier four, so a
 *     card about a great city is a card about nothing.
 *   · **A right of passage.** 0.95 grants stand on the entire board at any
 *     moment — under one, shared between thirty crowns.
 *
 * What the board *does* produce, and what the deck is therefore built on:
 * fifteen simultaneous wars with twenty-four of thirty crowns in one; nine
 * bandit camps; ten provinces above 0.2 unrest and seven above 0.4; seventeen of
 * twenty-four trade nodes with a dominant power; twenty-eight hated pairs of
 * crowns and twenty-three fond ones; a hundred and thirty-one provinces with no
 * walls at all.
 */

import { chance, nextInt } from '../../core/rng.ts';
import { nationDef } from '../../data/nations.ts';
import { regionDef, regionOf } from '../../data/provinces.ts';
import { addOpinion, sameConfession } from './diplomacy.ts';
import { applyLosses, armiesOf, findArmy, rawPoints, removeArmy } from './armies.ts';
import { campAt, removeCamp } from './bandits.ts';
import { neighbours } from './graph.ts';
import { maxFortAt } from './holdings.ts';
import { controlIn, nodeName, tradeNode, tradeNodeOf } from './trade.ts';
import { Estate, ESTATE_NAMES, nudgeEstate, standing } from './estates.ts';
import { garrisonCap, nationName, provinceName } from './state.ts';
import {
  addUnrest,
  answeredBy,
  ctx,
  giveGold,
  giveManpower,
  heldBy,
  plural,
  takeManpower,
  type RealmEventCtx,
  type RealmEventDef,
} from './chronicletypes.ts';
import { CONQUEST_UNREST, type Army, type CampaignState } from './types.ts';

// ── Readers the gates share ────────────────────────────────────────────────

/** Provinces this crown holds inside one region, in index order. */
function heldInRegion(c: CampaignState, nation: number, region: number): number[] {
  const out: number[] = [];
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    if (regionOf(p) !== region) continue;
    out.push(p);
  }
  return out;
}

/**
 * The region this crown has most of itself in, or -1.
 *
 * Ties break on the lower region index, which is arbitrary and — far more
 * importantly — stable. Eleven regions on the board and a typical crown holds
 * five provinces, so this is nearly always a real answer rather than a
 * technicality.
 */
function heartland(c: CampaignState, nation: number, least: number): number {
  const counts = new Map<number, number>();
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].owner !== nation) continue;
    const r = regionOf(p);
    counts.set(r, (counts.get(r) ?? 0) + 1);
  }
  let best = -1;
  let bestN = least - 1;
  for (const [r, n] of [...counts.entries()].sort((a, b) => a[0] - b[0])) {
    if (n > bestN) {
      bestN = n;
      best = r;
    }
  }
  return best;
}

/** How many provinces a crown holds. Cheap enough to call from a gate. */
function holdingsOf(c: CampaignState, nation: number): number {
  let n = 0;
  for (const p of c.provinces) if (p.owner === nation) n++;
  return n;
}

/** Living crowns other than this one, in index order. */
function others(c: CampaignState, nation: number): number[] {
  const out: number[] = [];
  for (let n = 0; n < c.nations.length; n++) {
    if (n !== nation && c.nations[n].alive) out.push(n);
  }
  return out;
}

/** Strength points a crown has standing on the board. */
function fielded(c: CampaignState, nation: number): number {
  let p = 0;
  for (const a of c.armies) if (a.owner === nation) p += rawPoints(a);
  return p;
}

/** The temperament of the crown this is happening to. */
function temper(c: CampaignState, nation: number) {
  return nationDef(c.nations[nation].def);
}

/** What ground is like where this happened, in the region table's own words. */
function groundOf(region: number): string {
  return regionDef(region).character;
}

/**
 * A host with nothing in it, for the two places that price a choice off an army
 * that may have been destroyed between the draw and the answer.
 *
 * `rawPoints` only ever walks `regiments`, so an empty one is exactly zero, and
 * the alternative — a cast through `never` at each site — is a lie the type
 * system would let through and a crash the moment somebody read a second field.
 */
function blankHost(): Army {
  return EMPTY_HOST;
}

const EMPTY_HOST: Army = {
  id: -1,
  owner: -1,
  sheltered: false,
  at: -1,
  moveTo: -1,
  moveT: 0,
  moveFrom: -1,
  moveTotal: 0,
  dest: -1,
  intercepting: -1,
  regroup: 0,
  regiments: [],
  stance: 0,
  siegeTurns: 0,
  supply: 1,
  victories: 0,
  objective: -1,
  idleTurns: 0,
  aboard: -1,
  trespassT: 0,
  name: 'nobody',
};

// ── The deck ───────────────────────────────────────────────────────────────

export const REALM_EVENTS: RealmEventDef[] = [
  // ── The crown ────────────────────────────────────────────────────────────

  /**
   * Somebody has declared war on you, and you hear it from the herald.
   *
   * > *"the events, and war declartion etc, needs to appear right infron of you
   * > in eu4 eu5 style event window"*
   *
   * A declaration was a line in the chronicle — the same weight as a harvest
   * report, on a panel nobody is looking at when it lands. It is the single
   * most consequential thing another crown can do to you, and the games this is
   * drawn from all stop the world for it.
   *
   * **Raised directly by `declareWar` rather than waiting for the deck's own
   * cadence.** Every other card here is a thing the realm *might* say this turn
   * and is picked by weight; this one is a thing that has already happened, and
   * a card about it that arrived three turns later would be a strange kind of
   * news. `weight: 0` keeps it out of the ordinary draw entirely — it is only
   * ever opened by name.
   *
   * The choices are deliberately not "accept / decline", because there is
   * nothing to decline: the war is declared either way. What is being asked is
   * *how the realm takes it*, which is an estate question and a real one — a
   * crown that answers a declaration by calling the banners is a different
   * crown from one that goes to the merchants for a war chest.
   */
  {
    id: 'war_declared',
    kind: 'crown',
    /**
     * A real weight, though it is opened by name.
     *
     * The first cut used `weight: 0` to keep it out of the ordinary draw, and
     * `tests/realmevents.test.ts` refused it three ways at once — the deck's
     * standing invariant is that **every card in it is a card the board can
     * actually produce**, which is what stops a card being added, never fired,
     * and quietly rotting. A zero-weight card is unreachable by construction
     * and would have sat there for ever.
     *
     * It does not need to be unreachable. Its `when` already answers "has
     * somebody declared war on this crown *this turn*", which is true for
     * exactly one turn and only when it has happened — so the ordinary pass can
     * only ever pick it in the same turn `declareWar` would have opened it by
     * name. The two doors lead to the same news.
     */
    weight: 7,
    repeat: 3,
    when: (c, n) => {
      /**
       * The freshest war declared *on* this crown while the news is still news.
       *
       * The window is a turn wide rather than exact, and that is not slack: the
       * chronicle's pass and the diplomatic pass both run inside a turn, and the
       * chronicle runs **first**. A card asking for `startTurn === c.turn` can
       * therefore never be true when the deck looks at it — which is precisely
       * what `tests/realmevents.test.ts` found, by walking a long campaign and
       * reporting that the board had never once produced this card.
       *
       * `forceRealmEvent` at the declaration itself is unaffected either way;
       * this is the door the ordinary draw comes through.
       */
      for (let i = c.diplo.wars.length - 1; i >= 0; i--) {
        const w = c.diplo.wars[i];
        if (w.defender !== n) continue;
        if (c.turn - w.startTurn > 1) continue;
        if (w.attacker === n) continue;
        return ctx(n, { other: w.attacker });
      }
      return null;
    },
    title: 'A Declaration',
    summary: 'A herald has come, and he did not take his hat off.',
    flavour: (c, k) =>
      `${nationName(c, k.other ?? 0)} has declared war. The herald read it out in the hall ` +
      `without sitting down, and was gone before the ink was dry on the copy he left behind. ` +
      `The council is already arguing about it and the whole realm will know by the week's end.`,
    choices: [
      {
        id: 'call_the_banners',
        label: 'Call the banners. Let them come and find us ready.',
        rules: () => 'The Hosts +16, the Great Families −8. Unrest −0.04 across your holdings.',
        ai: () => 0.6,
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Hosts, 16);
          nudgeEstate(c, k.nation, Estate.Families, -8);
          for (const p of heldBy(c, k.nation)) {
            c.provinces[p].unrest = Math.max(0, c.provinces[p].unrest - 0.04);
          }
          return `The banners are called. Whatever else this war is, it will not find us asleep.`;
        },
      },
      {
        id: 'open_the_coffers',
        label: 'Open the coffers. Wars are bought before they are fought.',
        cost: (c, k) => ({ gold: 30 + 12 * holdingsOf(c, k.nation) }),
        rules: (c, k) =>
          `Costs ${30 + 12 * holdingsOf(c, k.nation)} gold. The Merchants +14, the Hosts −4.`,
        ai: (c, k) => 0.35 + temper(c, k.nation).caution * 0.4,
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Merchants, 14);
          nudgeEstate(c, k.nation, Estate.Hosts, -4);
          return `The war chest is open. The merchants are content, which is its own kind of army.`;
        },
      },
      {
        id: 'say_nothing',
        label: 'Say nothing, and let the country find out on its own.',
        rules: () => 'Nothing is spent and nothing is promised. Unrest +0.05, the Hosts −6.',
        ai: () => 0.2,
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Hosts, -6);
          for (const p of heldBy(c, k.nation)) {
            c.provinces[p].unrest = Math.min(1, c.provinces[p].unrest + 0.05);
          }
          return `No proclamation is made. The news travels anyway, and badly.`;
        },
      },
    ],
  },

  {
    id: 'succession',
    kind: 'crown',
    title: 'The Seat Passes',
    summary: 'There is an empty chair and more than one claim on it.',
    weight: 5,
    repeat: 3,
    when: (c, n) => {
      if (c.turn < 30) return null;
      const held = holdingsOf(c, n);
      if (held < 2) return null;
      // The seat itself, so the flavour has somewhere to stand. The capital if we
      // still hold it, otherwise the lowest-indexed province we do.
      const seat = heldBy(c, n);
      if (seat.length === 0) return null;
      return ctx(n, { province: seat[0] });
    },
    flavour: (c, k) => {
      const held = holdingsOf(c, k.nation);
      return (
        `The chair at ${provinceName(k.province)} is empty and there are three papers on the table, ` +
        `each of them older than the last and each of them signed. ${plural(held, 'holding')} ` +
        `answer to whoever ends up sitting in it, and every one of them has sent somebody to ` +
        `stand at the back of the hall and watch how this goes.`
      );
    },
    choices: [
      {
        id: 'crown_the_eldest',
        label: 'Crown the eldest, and pay the others to be gracious.',
        cost: (c, k) => ({ gold: 40 + 22 * holdingsOf(c, k.nation) }),
        rules: (c, k) =>
          `The Great Families +18, the Hosts −6. No unrest. Costs ` +
          `${40 + 22 * holdingsOf(c, k.nation)} gold in settlements.`,
        ai: (c, k) => 0.5 + temper(c, k.nation).caution * 0.6,
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Families, 18);
          nudgeEstate(c, k.nation, Estate.Hosts, -6);
          return `The eldest sits. The others were paid, in front of witnesses, and went home rich and quiet.`;
        },
      },
      {
        id: 'let_the_hosts_decide',
        label: 'Let the hosts decide it.',
        rules: (c, k) =>
          `The Hosts +24, the Great Families −20, the Temple −8. ` +
          `+0.18 unrest across ${plural(holdingsOf(c, k.nation), 'holding')}.`,
        ai: (c, k) => 0.3 + temper(c, k.nation).militarism * 0.9 - temper(c, k.nation).caution * 0.4,
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Hosts, 24);
          nudgeEstate(c, k.nation, Estate.Families, -20);
          nudgeEstate(c, k.nation, Estate.Temple, -8);
          const n = addUnrest(c, heldBy(c, k.nation), 0.18);
          return `The captains settled it in an afternoon and a courtyard. ${plural(n, 'holding')} heard about it by the end of the week.`;
        },
      },
      {
        id: 'regency',
        label: 'A regency, and no decision at all.',
        rules: () =>
          'The Temple +14, the Hosts −14, the Great Families −8. A quarter of the manpower pool goes home.',
        ai: (c, k) => 0.35 + temper(c, k.nation).caution * 0.5 - temper(c, k.nation).aggression * 0.3,
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Temple, 14);
          nudgeEstate(c, k.nation, Estate.Hosts, -14);
          nudgeEstate(c, k.nation, Estate.Families, -8);
          const gone = takeManpower(c, k.nation, c.nations[k.nation].manpower * 0.25);
          return `Nobody sits. A council of seven sits instead, and ${gone} men who had been promised a war went back to their fields.`;
        },
      },
    ],
  },

  {
    id: 'envoy_at_the_gate',
    kind: 'crown',
    title: 'A Rival’s Envoy',
    summary: 'Somebody who does not like us has sent somebody who does not either.',
    weight: 5,
    repeat: 4,
    when: (c, n) => {
      // The crown that thinks worst of us, provided it thinks badly enough to be
      // worth a scene. Measured: 27.7 pairs on the board sit at or below −40, so
      // a typical crown has about one of these at any time.
      let worst = -1;
      let worstOp = -24;
      for (const o of others(c, n)) {
        const op = c.nations[o].relations[n];
        if (op < worstOp) {
          worstOp = op;
          worst = o;
        }
      }
      if (worst < 0) return null;
      return ctx(n, { other: worst });
    },
    flavour: (c, k) => {
      const them = nationName(c, k.other);
      const op = c.nations[k.other].relations[k.nation];
      const war = c.nations[k.nation].atWar[k.other] === 1;
      return (
        `He does not dismount, and he reads it off a paper rather than saying it, which is ` +
        `its own kind of insult. ${them} think of ${nationName(c, k.nation)} at ${Math.round(op)} ` +
        `out of a hundred${war ? ', and there is already a war on' : ', and there is no war on yet'}. ` +
        `He has been told to find out what that is worth.`
      );
    },
    choices: [
      {
        id: 'pay_him',
        label: 'Hear him, and give him what he came for.',
        cost: () => ({ gold: 180 }),
        rules: (c, k) =>
          `180 gold. ${nationName(c, k.other)} think +26 of us. The Hosts −16, the Great Families −10.`,
        ai: (c, k) => {
          const mine = fielded(c, k.nation);
          const theirs = fielded(c, k.other);
          // Weakness buys peace. A crown that cannot fight is glad of the price.
          return 0.2 + temper(c, k.nation).caution * 0.7 + (theirs > mine * 1.4 ? 0.6 : 0);
        },
        apply: (c, k) => {
          addOpinion(c, k.other, k.nation, 26);
          nudgeEstate(c, k.nation, Estate.Hosts, -16);
          nudgeEstate(c, k.nation, Estate.Families, -10);
          return `He got what he came for and did not thank anybody for it. ${nationName(c, k.other)} will be quiet a while.`;
        },
      },
      {
        id: 'refuse',
        label: 'Hear him, and refuse him.',
        rules: (c, k) => `${nationName(c, k.other)} think −8 of us. The Hosts +12.`,
        ai: (c, k) => 0.55 + temper(c, k.nation).caution * 0.2,
        apply: (c, k) => {
          addOpinion(c, k.other, k.nation, -8);
          nudgeEstate(c, k.nation, Estate.Hosts, 12);
          return `He was heard out to the last line of it, given a meal, and sent back with nothing.`;
        },
      },
      {
        id: 'mule',
        label: 'Send him back on a mule, facing the wrong way.',
        rules: (c, k) =>
          `${nationName(c, k.other)} think −32 of us and will remember it. ` +
          `The Hosts +22, the Temple −12. Every crown of our confession thinks +5 of us.`,
        ai: (c, k) =>
          0.15 + temper(c, k.nation).aggression * 1.1 - temper(c, k.nation).caution * 0.5,
        apply: (c, k) => {
          addOpinion(c, k.other, k.nation, -32);
          nudgeEstate(c, k.nation, Estate.Hosts, 22);
          nudgeEstate(c, k.nation, Estate.Temple, -12);
          for (const o of others(c, k.nation)) {
            if (o !== k.other && sameConfession(c, o, k.nation)) addOpinion(c, o, k.nation, 5);
          }
          return `He went back the way he came, backwards, and the story was in three kingdoms before he was.`;
        },
      },
    ],
  },

  {
    id: 'envoy_answered',
    kind: 'crown',
    title: 'They Answered The Mule',
    summary: 'The crown we humiliated has finished thinking about it.',
    weight: 7,
    repeat: 2,
    when: (c, n) => {
      // The chain. Reads only the history, which is already hashed and saved, so
      // a consequence arriving two years after the decision costs no new state.
      for (const o of others(c, n)) {
        if (!answeredBy(c, n, 'envoy_at_the_gate', 'mule')) break;
        // Whoever is angriest with us now; the insult was public and more than
        // one crown enjoyed it.
        if (c.nations[o].relations[n] <= -30) return ctx(n, { other: o });
      }
      return null;
    },
    flavour: (c, k) =>
      `${nationName(c, k.other)} did not write back. They have been counting instead, and the ` +
      `counting is finished: ${plural(c.armies.filter((a) => a.owner === k.other).length, 'host')} ` +
      `and a border they have stopped patrolling, which is what a crown does when it has decided ` +
      `it no longer needs to know what is on the other side.`,
    choices: [
      {
        id: 'buy_it_back',
        label: 'Buy the peace now, and dearly.',
        cost: () => ({ gold: 200 }),
        rules: (c, k) => `200 gold. ${nationName(c, k.other)} think +45 of us. The Hosts −12.`,
        // Priced at 200 rather than 300, and weighted off the *board* rather than
        // temperament alone: the crown that should buy its way out is the one
        // that has been counting hulls and hosts across the border and does not
        // like the arithmetic. At 300 flat it was never once taken.
        ai: (c, k) =>
          0.35 +
          temper(c, k.nation).caution * 0.8 +
          (fielded(c, k.other) > fielded(c, k.nation) * 1.5 ? 0.7 : 0),
        apply: (c, k) => {
          addOpinion(c, k.other, k.nation, 45);
          nudgeEstate(c, k.nation, Estate.Hosts, -12);
          return `Three hundred gold and a written apology nobody read aloud. It worked, which was the humiliating part.`;
        },
      },
      {
        id: 'stand_to',
        label: 'Stand to, and let them come.',
        rules: (c, k) =>
          `Every garrison of ours goes to its cap — ${plural(holdingsOf(c, k.nation), 'holding')}. ` +
          `Costs 60 men out of the pool. The Hosts +20, the Great Families +8.`,
        ai: (c, k) => 0.4 + temper(c, k.nation).militarism * 0.7,
        apply: (c, k) => {
          const held = heldBy(c, k.nation);
          for (const p of held) c.provinces[p].garrison = garrisonCap(c, p);
          takeManpower(c, k.nation, 60);
          nudgeEstate(c, k.nation, Estate.Hosts, 20);
          nudgeEstate(c, k.nation, Estate.Families, 8);
          return `Every wall on the frontier was manned to its watch-list inside the fortnight. ${plural(held.length, 'holding')} slept badly and slept armed.`;
        },
      },
      {
        id: 'marry_it',
        label: 'Offer them a marriage and eat the joke about the mule.',
        rules: (c, k) =>
          `${nationName(c, k.other)} think +20 of us. The Great Families −16, the Hosts −8.`,
        // Wanted by exactly the crown that should want it: cautious, not
        // militarist, and already fond enough of them to be embarrassed. Measured
        // at zero takes before this term existed, which is a card face nobody
        // would ever have seen the AI turn over.
        ai: (c, k) =>
          0.75 + temper(c, k.nation).caution * 0.6 - temper(c, k.nation).militarism * 0.7,
        apply: (c, k) => {
          addOpinion(c, k.other, k.nation, 20);
          nudgeEstate(c, k.nation, Estate.Families, -16);
          nudgeEstate(c, k.nation, Estate.Hosts, -8);
          return `A cousin nobody could name was married to a cousin nobody could name, and the mule was mentioned at the feast by somebody who had been drinking.`;
        },
      },
    ],
  },

  {
    id: 'ground_remembered',
    kind: 'crown',
    title: 'They Have Not Forgotten It',
    summary: 'There are exiles in the hall from ground we no longer hold.',
    weight: 4,
    repeat: 3,
    when: (c, n) => {
      // `claimedBy` is the memory of the last owner and the game's whole claim
      // system. Lowest province index, so a replay is about the same ground.
      for (let p = 0; p < c.provinces.length; p++) {
        const prov = c.provinces[p];
        if (prov.claimedBy !== n) continue;
        if (prov.owner === n || prov.owner < 0) continue;
        if (!c.nations[prov.owner]?.alive) continue;
        return ctx(n, { province: p, other: prov.owner });
      }
      return null;
    },
    flavour: (c, k) =>
      `They have come from ${provinceName(k.province)}, which ${nationName(c, k.other)} have been ` +
      `holding since it stopped being ours, and they have brought the register, the seal and a boy ` +
      `who is apparently the rightful something. The ground is ${groundOf(regionOf(k.province))} ` +
      `country and worth having. That is not why they are here, but it does not hurt.`,
    choices: [
      {
        id: 'press',
        label: 'Press the claim, loudly.',
        rules: (c, k) =>
          `${nationName(c, k.other)} think −22 of us. The Hosts +20, the Great Families +18.`,
        ai: (c, k) => 0.35 + temper(c, k.nation).aggression * 0.9,
        apply: (c, k) => {
          addOpinion(c, k.other, k.nation, -22);
          nudgeEstate(c, k.nation, Estate.Hosts, 20);
          nudgeEstate(c, k.nation, Estate.Families, 18);
          return `The claim was read out at the border post, in full, twice, to a sergeant who could not read.`;
        },
      },
      {
        id: 'renounce',
        label: 'Renounce it in writing, before witnesses.',
        rules: (c, k) =>
          `${nationName(c, k.other)} think +32 of us. The Great Families −26, the Hosts −12. ` +
          `−0.1 unrest across ${plural(holdingsOf(c, k.nation), 'holding')}.`,
        ai: (c, k) => 0.2 + temper(c, k.nation).caution * 0.8 - temper(c, k.nation).aggression * 0.4,
        apply: (c, k) => {
          addOpinion(c, k.other, k.nation, 32);
          nudgeEstate(c, k.nation, Estate.Families, -26);
          nudgeEstate(c, k.nation, Estate.Hosts, -12);
          addUnrest(c, heldBy(c, k.nation), -0.1);
          return `Signed, sealed and sent. The exiles were in the hall when it was read and were not asked to leave.`;
        },
      },
      {
        id: 'pension',
        label: 'Pension the exiles and say nothing either way.',
        cost: () => ({ gold: 120 }),
        rules: () => '120 gold. The Great Families +12. Nobody else notices.',
        ai: (c, k) => 0.4 + temper(c, k.nation).caution * 0.3,
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Families, 12);
          return `They were housed, fed, and never once put in front of anybody who mattered. The boy is being taught letters.`;
        },
      },
    ],
  },

  // ── The country ──────────────────────────────────────────────────────────

  {
    id: 'bad_harvest',
    kind: 'country',
    title: 'The Year The Rain Did Not Stop',
    summary: 'A whole region has come in short.',
    weight: 6,
    repeat: 4,
    when: (c, n) => {
      const r = heartland(c, n, 2);
      if (r < 0) return null;
      return ctx(n, { region: r, province: heldInRegion(c, n, r)[0] ?? -1 });
    },
    flavour: (c, k) => {
      const held = heldInRegion(c, k.nation, k.region);
      return (
        `It is ${plural(held.length, 'holding')} across ${regionDef(k.region).name}, all of them ` +
        `at once, which is how you know it is the weather and not the reeves. ` +
        `${groundOf(k.region) === 'marsh' || groundOf(k.region) === 'blight' ? 'This ground was never going to carry a bad year.' : 'The ground is good here, which is what makes it frightening.'} ` +
        `The carts came in and the reckoning was done twice and the second count was worse.`
      );
    },
    choices: [
      {
        id: 'open_the_yards',
        label: 'Open the yards and feed them out of the crown’s own stores.',
        cost: (c, k) => ({ gold: 30 * Math.max(1, heldInRegion(c, k.nation, k.region).length) }),
        rules: (c, k) => {
          const held = heldInRegion(c, k.nation, k.region);
          return (
            `${30 * Math.max(1, held.length)} gold. −0.2 unrest across ${plural(held.length, 'holding')} in ` +
            `${regionDef(k.region).name}. The Great Families +14, the Temple +10.`
          );
        },
        ai: (c, k) => 0.45 + temper(c, k.nation).caution * 0.5,
        apply: (c, k) => {
          const held = heldInRegion(c, k.nation, k.region);
          addUnrest(c, held, -0.2);
          nudgeEstate(c, k.nation, Estate.Families, 14);
          nudgeEstate(c, k.nation, Estate.Temple, 10);
          return `The yards were opened across ${regionDef(k.region).name} and stayed open until spring. It was expensive and nobody starved.`;
        },
      },
      {
        id: 'let_them_eat_what_they_have',
        label: 'Let them eat what they have.',
        rules: (c, k) => {
          const held = heldInRegion(c, k.nation, k.region);
          return (
            `+0.26 unrest across ${plural(held.length, 'holding')} in ${regionDef(k.region).name}. ` +
            `The Great Families −16, the Temple −14. The purse is untouched.`
          );
        },
        ai: (c, k) => 0.2 + (1 - temper(c, k.nation).caution) * 0.5,
        apply: (c, k) => {
          const held = heldInRegion(c, k.nation, k.region);
          addUnrest(c, held, 0.26);
          nudgeEstate(c, k.nation, Estate.Families, -16);
          nudgeEstate(c, k.nation, Estate.Temple, -14);
          return `Nothing was done. ${regionDef(k.region).name} got through it, mostly, and has written down that nothing was done.`;
        },
      },
      {
        id: 'buy_it_in',
        label: 'Buy grain in from whoever will sell it.',
        cost: () => ({ gold: 210 }),
        rules: (_c, k) =>
          `210 gold. −0.14 unrest in ${regionDef(k.region).name}. The Counting Houses +18. ` +
          `Whoever sold to us thinks +10 of us.`,
        ai: (c, k) => 0.35 + standing(c, k.nation, Estate.Merchants) / 220,
        apply: (c, k) => {
          addUnrest(c, heldInRegion(c, k.nation, k.region), -0.14);
          nudgeEstate(c, k.nation, Estate.Merchants, 18);
          // Whoever thinks best of us and is not at war with us. Index order
          // among equals, so a replay buys from the same crown.
          let seller = -1;
          let bestOp = -101;
          for (const o of others(c, k.nation)) {
            if (c.nations[k.nation].atWar[o] === 1) continue;
            const op = c.nations[o].relations[k.nation];
            if (op > bestOp) {
              bestOp = op;
              seller = o;
            }
          }
          if (seller >= 0) {
            addOpinion(c, seller, k.nation, 10);
            return `The carts came in from ${nationName(c, seller)} at a price that will be remembered on both sides of it.`;
          }
          return `The carts came in from nobody anybody would name, at a price nobody would repeat.`;
        },
      },
    ],
  },

  {
    id: 'camp_sends_a_man',
    kind: 'country',
    title: 'The Camp Sends A Man Down',
    summary: 'The brigands would like to negotiate.',
    weight: 5,
    repeat: 4,
    when: (c, n) => {
      for (const k of c.camps) {
        if (c.provinces[k.at]?.owner !== n) continue;
        return ctx(n, { province: k.at });
      }
      return null;
    },
    flavour: (c, k) => {
      const camp = campAt(c, k.province);
      const age = camp ? c.turn - camp.founded : 0;
      return (
        `He comes down from the hills above ${provinceName(k.province)} under a rag on a stick and ` +
        `asks for the reeve by name, which he should not know. There have been ` +
        `${plural(Math.round(camp?.strength ?? 0), 'man')} up there for ${plural(age, 'month')} ` +
        `and he has an offer, and the offer is not unreasonable, and that is the worst of it.`
      );
    },
    choices: [
      {
        id: 'pay_them_off',
        label: 'Pay them to be somewhere else.',
        cost: (c, k) => ({ gold: Math.round(60 + (campAt(c, k.province)?.strength ?? 0) * 0.5) }),
        rules: (c, k) =>
          `${Math.round(60 + (campAt(c, k.province)?.strength ?? 0) * 0.5)} gold. The camp at ` +
          `${provinceName(k.province)} breaks up. The Hosts −18, the Counting Houses +16.`,
        ai: (c, k) => 0.4 + temper(c, k.nation).caution * 0.4,
        apply: (c, k) => {
          removeCamp(c, k.province);
          nudgeEstate(c, k.nation, Estate.Hosts, -18);
          nudgeEstate(c, k.nation, Estate.Merchants, 16);
          return `They were gone inside a week, and the road above ${provinceName(k.province)} has been quiet since. Nobody asks where they went.`;
        },
      },
      {
        id: 'his_hand_and_his_head',
        label: 'Take his hand, and then his head.',
        rules: (_c, k) =>
          `The camp at ${provinceName(k.province)} grows by a quarter. The Hosts +18, the Temple +8, ` +
          `the Counting Houses −12.`,
        ai: (c, k) => 0.2 + temper(c, k.nation).aggression * 1.0,
        apply: (c, k) => {
          const camp = campAt(c, k.province);
          if (camp) camp.strength = Math.round(camp.strength * 1.25);
          nudgeEstate(c, k.nation, Estate.Hosts, 18);
          nudgeEstate(c, k.nation, Estate.Temple, 8);
          nudgeEstate(c, k.nation, Estate.Merchants, -12);
          return `Both, in the market at ${provinceName(k.province)}, on the same afternoon. The hills answered within the month and answered larger.`;
        },
      },
      {
        id: 'hire_them',
        label: 'Take them into the levy and ask nothing about the last two years.',
        rules: (_c, k) =>
          `The camp at ${provinceName(k.province)} breaks up and its strength joins the manpower pool. ` +
          `+0.22 unrest at ${provinceName(k.province)}. The Great Families −18, the Temple −16, the Hosts +10.`,
        ai: (c, k) => 0.3 + temper(c, k.nation).militarism * 0.7,
        apply: (c, k) => {
          const camp = campAt(c, k.province);
          const men = giveManpower(c, k.nation, camp ? camp.strength : 0);
          removeCamp(c, k.province);
          addUnrest(c, [k.province], 0.22);
          nudgeEstate(c, k.nation, Estate.Families, -18);
          nudgeEstate(c, k.nation, Estate.Temple, -16);
          nudgeEstate(c, k.nation, Estate.Hosts, 10);
          return `${plural(men, 'man')} came down out of the hills and were given colours. ${provinceName(k.province)} has to live next to them now.`;
        },
      },
    ],
  },

  {
    id: 'unwalled_march',
    kind: 'country',
    title: 'The March With Nothing On It',
    summary: 'A frontier holding has no walls and a bad neighbour.',
    weight: 5,
    repeat: 4,
    when: (c, n) => {
      // Measured: 131 of 191 provinces carry no fortification at all, and 24 of
      // 30 crowns are at war in a typical month, so this is a common position
      // rather than a contrived one.
      for (let p = 0; p < c.provinces.length; p++) {
        const prov = c.provinces[p];
        if (prov.owner !== n || prov.fort > 0) continue;
        for (const q of neighbours(c, p)) {
          const owner = c.provinces[q].owner;
          if (owner < 0 || owner === n || !c.nations[owner]?.alive) continue;
          const hostile = c.nations[n].atWar[owner] === 1 || c.nations[owner].relations[n] <= -20;
          if (hostile) return ctx(n, { province: p, other: owner });
        }
      }
      return null;
    },
    flavour: (c, k) =>
      `${provinceName(k.province)} has a ditch, a hedge and an argument about whose job the hedge is. ` +
      `Across it is ${nationName(c, k.other)}, who think ${Math.round(c.nations[k.other].relations[k.nation])} ` +
      `of us${c.nations[k.nation].atWar[k.other] === 1 ? ' and are already at war with us' : ''}. ` +
      `The reeve has stopped writing letters about it and has started writing to somebody else about it.`,
    choices: [
      {
        id: 'wall_it',
        label: 'Wall it. This year, not next.',
        cost: () => ({ gold: 200 }),
        rules: (_c, k) =>
          `200 gold. ${provinceName(k.province)} raises walls to 1. The Great Families +14, the Counting Houses −10.`,
        ai: (c, k) => 0.4 + temper(c, k.nation).caution * 0.9,
        apply: (c, k) => {
          const prov = c.provinces[k.province];
          if (prov.fort < maxFortAt(c, k.province)) prov.fort++;
          nudgeEstate(c, k.nation, Estate.Families, 14);
          nudgeEstate(c, k.nation, Estate.Merchants, -10);
          return `The hedge came out and a wall went up behind it. ${provinceName(k.province)} is a different proposition to walk into now.`;
        },
      },
      {
        id: 'garrison_it',
        label: 'Put men on it instead. Walls take years.',
        rules: (_c, k) =>
          `Costs 80 men out of the pool. ${provinceName(k.province)} goes to its garrison cap. The Hosts +14.`,
        ai: (c, k) => 0.4 + temper(c, k.nation).militarism * 0.6,
        apply: (c, k) => {
          takeManpower(c, k.nation, 80);
          c.provinces[k.province].garrison = garrisonCap(c, k.province);
          nudgeEstate(c, k.nation, Estate.Hosts, 14);
          return `Eighty men and a sergeant who has been there before. No wall, but somebody is awake on it.`;
        },
      },
      {
        id: 'let_it_burn',
        label: 'Let it burn if it burns.',
        rules: (_c, k) =>
          `+0.3 unrest at ${provinceName(k.province)}. The Great Families −22, the Counting Houses +14. ` +
          `+90 gold not spent on it.`,
        ai: (c, k) => 0.15 + (1 - temper(c, k.nation).caution) * 0.6,
        apply: (c, k) => {
          addUnrest(c, [k.province], 0.3);
          nudgeEstate(c, k.nation, Estate.Families, -22);
          nudgeEstate(c, k.nation, Estate.Merchants, 14);
          giveGold(c, k.nation, 90);
          return `The letters were filed. ${provinceName(k.province)} was told, in writing, that it was not a priority, which was a mistake in the writing rather than in the policy.`;
        },
      },
    ],
  },

  // ── The estates ──────────────────────────────────────────────────────────

  {
    id: 'temple_asks',
    kind: 'estate',
    title: 'The Confession Asks For Its Due',
    summary: 'The temple has sent a list rather than a request.',
    weight: 5,
    repeat: 4,
    when: (c, n) => {
      if (holdingsOf(c, n) < 3) return null;
      if (standing(c, n, Estate.Temple) >= 45) return null;
      const held = heldBy(c, n);
      if (held.length === 0) return null;
      return ctx(n, { province: held[Math.min(1, held.length - 1)] });
    },
    flavour: (c, k) =>
      `They want a house at ${provinceName(k.province)}, and the tithe of the ground around it, and ` +
      `the naming of the reeve. They currently stand at ${Math.round(standing(c, k.nation, Estate.Temple))} ` +
      `with the crown, which they mention. They mention it twice.`,
    choices: [
      {
        id: 'build_it',
        label: 'Build them what they asked for.',
        cost: () => ({ gold: 240 }),
        rules: () => '240 gold. The Temple +32, the Counting Houses −12.',
        ai: (c, k) => 0.35 + (c.nations[k.nation].treasury.gold > 500 ? 0.5 : 0),
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Temple, 32);
          nudgeEstate(c, k.nation, Estate.Merchants, -12);
          return `Stone, glass and a roof that took two winters. ${ESTATE_NAMES[Estate.Temple]} have not asked for anything since, which is new.`;
        },
      },
      {
        id: 'give_the_tithe',
        label: 'Give them the tithe instead. It costs nothing today.',
        rules: (c, k) =>
          `The Temple +24, the Counting Houses −20, the Great Families −12. ` +
          `−0.12 unrest across ${plural(holdingsOf(c, k.nation), 'holding')}.`,
        ai: (c, k) => 0.45 + (c.nations[k.nation].treasury.gold < 200 ? 0.4 : 0),
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Temple, 24);
          nudgeEstate(c, k.nation, Estate.Merchants, -20);
          nudgeEstate(c, k.nation, Estate.Families, -12);
          addUnrest(c, heldBy(c, k.nation), -0.12);
          return `Signed away in an afternoon by somebody who will not be here when the bill arrives.`;
        },
      },
      {
        id: 'refuse_in_writing',
        label: 'Refuse. In writing. With reasons.',
        rules: (c, k) =>
          `The Temple −28, the Counting Houses +14. ` +
          `+0.14 unrest across ${plural(holdingsOf(c, k.nation), 'holding')}.`,
        // A crown that has already kept the temple sweet can afford to say no
        // once, and a crown with no coin has to. Both terms, or the AI simply
        // never refuses and the card has two faces instead of three.
        ai: (c, k) =>
          0.3 +
          Math.max(0, standing(c, k.nation, Estate.Temple)) / 120 +
          (c.nations[k.nation].treasury.gold < 150 ? 0.35 : 0),
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Temple, -28);
          nudgeEstate(c, k.nation, Estate.Merchants, 14);
          addUnrest(c, heldBy(c, k.nation), 0.14);
          return `Four pages of reasons, every one of them true, read out from every pulpit in the realm by men who disagreed with all of them.`;
        },
      },
    ],
  },

  {
    id: 'the_ledger',
    kind: 'estate',
    title: 'The Ledger Does Not Balance',
    summary: 'The counting houses want a word about the arrears.',
    weight: 5,
    repeat: 4,
    when: (c, n) => {
      // Measured: the mean living crown holds 184 gold, so an empty purse is an
      // ordinary position rather than a disaster state.
      if (c.nations[n].treasury.gold >= 130) return null;
      if (holdingsOf(c, n) < 3) return null;
      return ctx(n, { province: heldBy(c, n)[0] ?? -1 });
    },
    flavour: (c, k) =>
      `They put the book on the table, open, at the page, and say nothing at all, which is worse ` +
      `than shouting. There are ${Math.round(c.nations[k.nation].treasury.gold)} gold in the ` +
      `treasury of a crown holding ${plural(holdingsOf(c, k.nation), 'province')}, and they have ` +
      `known that for longer than we have.`,
    choices: [
      {
        id: 'borrow',
        label: 'Borrow from them, on their terms.',
        rules: (c, k) =>
          `+320 gold. The Counting Houses +16, the Great Families −14. ` +
          `+0.12 unrest across ${plural(holdingsOf(c, k.nation), 'holding')} — they collect it themselves.`,
        ai: () => 0.6,
        apply: (c, k) => {
          giveGold(c, k.nation, 320);
          nudgeEstate(c, k.nation, Estate.Merchants, 16);
          nudgeEstate(c, k.nation, Estate.Families, -14);
          addUnrest(c, heldBy(c, k.nation), 0.12);
          return `Three hundred and twenty gold, at a rate nobody wrote down, collected by men who brought their own ledgers and their own dogs.`;
        },
      },
      {
        id: 'debase',
        label: 'Debase the coin and hope nobody weighs it.',
        rules: (c, k) =>
          `+420 gold. The Counting Houses −30, the Temple −12. ` +
          `+0.2 unrest across ${plural(holdingsOf(c, k.nation), 'holding')}.`,
        ai: (c, k) => 0.25 + (1 - temper(c, k.nation).caution) * 0.5,
        apply: (c, k) => {
          giveGold(c, k.nation, 420);
          nudgeEstate(c, k.nation, Estate.Merchants, -30);
          nudgeEstate(c, k.nation, Estate.Temple, -12);
          addUnrest(c, heldBy(c, k.nation), 0.2);
          return `The new coin is the same size and lighter, and it was weighed in a market inside nine days.`;
        },
      },
      {
        id: 'sell_a_charter',
        label: 'Sell them a charter over one of our own holdings.',
        rules: (_c, k) =>
          `+240 gold. The Counting Houses +24, the Great Families −24. ` +
          `+0.24 unrest at ${k.province >= 0 ? provinceName(k.province) : 'the seat'}.`,
        ai: (c, k) => 0.35 + standing(c, k.nation, Estate.Merchants) / 260,
        apply: (c, k) => {
          giveGold(c, k.nation, 240);
          nudgeEstate(c, k.nation, Estate.Merchants, 24);
          nudgeEstate(c, k.nation, Estate.Families, -24);
          if (k.province >= 0) addUnrest(c, [k.province], 0.24);
          return `${k.province >= 0 ? provinceName(k.province) : 'The seat'} answers to a company now, in the small matters, which turn out to be most of them.`;
        },
      },
    ],
  },

  {
    id: 'two_houses_one_ford',
    kind: 'estate',
    title: 'Two Houses, One Ford',
    summary: 'The great families have brought a boundary dispute to the crown.',
    weight: 5,
    repeat: 4,
    when: (c, n) => {
      const held = heldBy(c, n);
      if (held.length < 4) return null;
      // The least-built holding, because that is where a dispute about a ford is
      // still worth having. Index order among equals.
      let worst = held[0];
      for (const p of held) if (c.provinces[p].tier < c.provinces[worst].tier) worst = p;
      return ctx(n, { province: worst });
    },
    flavour: (_c, k) =>
      `It is about the ford at ${provinceName(k.province)}, and then about the tolls, and then ` +
      `about a marriage in the last generation that one side says was never consummated and the ` +
      `other says produced the man currently standing at the back of the hall. Both houses have ` +
      `come to the crown, which means they have already tried everything else.`,
    choices: [
      {
        id: 'the_older_house',
        label: 'Find for the older house.',
        rules: (_c, k) =>
          `The Great Families +16. +0.18 unrest at ${provinceName(k.province)}.`,
        ai: (c, k) => 0.45 + temper(c, k.nation).caution * 0.3,
        apply: (c, k) => {
          nudgeEstate(c, k.nation, Estate.Families, 16);
          addUnrest(c, [k.province], 0.18);
          return `Precedent, parchment and four hundred years. The younger house left before the judgment was finished being read.`;
        },
      },
      {
        id: 'the_richer_house',
        label: 'Find for whoever offered more.',
        rules: (_c, k) =>
          `+160 gold. The Great Families −20, the Counting Houses +16. ` +
          `+0.12 unrest at ${provinceName(k.province)}.`,
        ai: (c, k) => 0.3 + (c.nations[k.nation].treasury.gold < 200 ? 0.5 : 0),
        apply: (c, k) => {
          giveGold(c, k.nation, 160);
          nudgeEstate(c, k.nation, Estate.Families, -20);
          nudgeEstate(c, k.nation, Estate.Merchants, 16);
          addUnrest(c, [k.province], 0.12);
          return `The judgment was sound, well-argued, correctly sealed, and had been paid for in advance by one of the parties.`;
        },
      },
      {
        id: 'take_the_ford',
        label: 'Take the ford for the crown and let them both hate us.',
        rules: (_c, k) =>
          `${provinceName(k.province)} goes to its garrison cap. The Great Families −26, the Hosts +14. ` +
          `Costs 50 men.`,
        ai: (c, k) => 0.25 + temper(c, k.nation).aggression * 0.6,
        apply: (c, k) => {
          c.provinces[k.province].garrison = garrisonCap(c, k.province);
          takeManpower(c, k.nation, 50);
          nudgeEstate(c, k.nation, Estate.Families, -26);
          nudgeEstate(c, k.nation, Estate.Hosts, 14);
          return `A crown toll-house on the ford at ${provinceName(k.province)}, with a garrison, and two houses who now agree about exactly one thing.`;
        },
      },
    ],
  },

  // ── The war ──────────────────────────────────────────────────────────────

  {
    id: 'host_will_not_march',
    kind: 'war',
    title: 'The Host Will Not Move',
    summary: 'A captain has run out of reasons and stopped inventing them.',
    weight: 6,
    repeat: 4,
    when: (c, n) => {
      // Measured: 3.9 of the board's 34 hosts carry four or more idle months in
      // a typical month, and a host that has stood still and a host that has not
      // are otherwise indistinguishable on the record.
      for (const a of armiesOf(c, n)) {
        if (a.idleTurns < 4 && a.supply >= 0.7) continue;
        return ctx(n, { army: a.id, province: a.at });
      }
      return null;
    },
    flavour: (c, k) => {
      const a = findArmy(c, k.army);
      if (!a) return 'The host has not moved.';
      return (
        `${a.name} has stood at ${provinceName(a.at)} for ${plural(a.idleTurns, 'month')} at ` +
        `${Math.round(a.supply * 100)} per cent of its provisioning, and this morning its captain ` +
        `sent back the order rather than the answer. He has written on it, in his own hand, that ` +
        `the men have not been paid and that he has run out of ways to say so politely.`
      );
    },
    choices: [
      {
        id: 'replace_him',
        label: 'Replace him before breakfast.',
        rules: (c, k) => {
          const a = findArmy(c, k.army);
          return (
            `${a?.name ?? 'The host'} loses two victories’ worth of veterancy and its idle count resets. ` +
            `The Hosts −20, the Great Families +8.`
          );
        },
        ai: (c, k) => 0.3 + temper(c, k.nation).aggression * 0.6,
        apply: (c, k) => {
          const a = findArmy(c, k.army);
          if (a) {
            a.victories = Math.max(0, a.victories - 2);
            a.idleTurns = 0;
          }
          nudgeEstate(c, k.nation, Estate.Hosts, -20);
          nudgeEstate(c, k.nation, Estate.Families, 8);
          return `He was replaced by a man who had been waiting for it. ${a?.name ?? 'The host'} marched the same afternoon and rather worse.`;
        },
      },
      {
        id: 'pay_the_arrears',
        label: 'Pay the arrears he is actually complaining about.',
        /**
         * Priced off the host's own arrears rather than flat, and priced *low* —
         * measured at zero takes across two hundred and sixty firings when it was
         * `60 + points × 0.12`, because a crown with a captain refusing orders is
         * exactly a crown that has no money. A choice the board can never afford
         * is a choice that is not on the card.
         */
        cost: (c, k) => ({ gold: Math.round(40 + rawPoints(findArmy(c, k.army) ?? blankHost()) * 0.05) }),
        rules: (c, k) => {
          const a = findArmy(c, k.army);
          const g = Math.round(40 + rawPoints(a ?? blankHost()) * 0.05);
          return `${g} gold. ${a?.name ?? 'The host'} is provisioned to full and its idle count resets. The Hosts +22.`;
        },
        ai: (c, k) => 0.75 + standing(c, k.nation, Estate.Hosts) / -300,
        apply: (c, k) => {
          const a = findArmy(c, k.army);
          if (a) {
            a.supply = 1;
            a.idleTurns = 0;
          }
          nudgeEstate(c, k.nation, Estate.Hosts, 22);
          return `Counted out on a table in front of the men who were owed it. ${a?.name ?? 'The host'} was on the road by the end of the week.`;
        },
      },
      {
        id: 'let_him_sit',
        label: 'Let him sit there and think about it.',
        rules: (c, k) => {
          const a = findArmy(c, k.army);
          return (
            `${a?.name ?? 'The host'} loses a tenth of its models to desertion. The Hosts +6, ` +
            `the Great Families −12. +0.16 unrest at ${provinceName(k.province)}.`
          );
        },
        ai: (c, k) => 0.25 + temper(c, k.nation).caution * 0.4,
        apply: (c, k) => {
          const a = findArmy(c, k.army);
          const lost = a ? applyLosses(a, 0.9) : 0;
          // A host desertion empties stops existing. Every other taker of
          // losses — hunger, battle, the sea — buries its dead armies at the
          // site; a card that leaves a zero-regiment ghost on the board leaves
          // it there *forever*, because the hunger sweep only reaps the starving.
          if (a && a.regiments.length === 0) removeArmy(c, a.id);
          if (k.province >= 0) addUnrest(c, [k.province], 0.16);
          nudgeEstate(c, k.nation, Estate.Hosts, 6);
          nudgeEstate(c, k.nation, Estate.Families, -12);
          return `Nothing was said and nothing was sent. ${plural(lost, 'man')} took the hint and went home, and the captain is still there.`;
        },
      },
    ],
  },

  {
    id: 'fourth_year',
    kind: 'war',
    title: 'The Fourth Year Of It',
    summary: 'A war has outlasted everybody’s reason for starting it.',
    weight: 6,
    repeat: 4,
    when: (c, n) => {
      // Measured: fifteen wars run on the board in a typical month across thirty
      // crowns, so a crown that has been at war for twenty months is common.
      for (const war of c.diplo.wars) {
        const attacking = war.attackers.includes(n);
        if (!attacking && !war.defenders.includes(n)) continue;
        if (c.turn - war.startTurn < 20) continue;
        const foe = attacking ? war.defender : war.attacker;
        if (!c.nations[foe]?.alive) continue;
        return ctx(n, { other: foe });
      }
      return null;
    },
    flavour: (c, k) => {
      const war = c.diplo.wars.find(
        (x) =>
          (x.attackers.includes(k.nation) && x.defenders.includes(k.other)) ||
          (x.defenders.includes(k.nation) && x.attackers.includes(k.other)),
      );
      const months = war ? c.turn - war.startTurn : 0;
      return (
        `${plural(months, 'month')} against ${nationName(c, k.other)}, ` +
        `${war ? `${war.battlesWon} battles won and ${war.battlesLost} lost` : 'nobody is counting'}, ` +
        `and the men who set out for it have been replaced twice. Nobody in the hall can now state ` +
        `the original grievance without reading it off something.`
      );
    },
    choices: [
      {
        id: 'new_levy',
        label: 'Raise a fresh levy on the strength of it.',
        cost: () => ({ gold: 160 }),
        rules: (c, k) =>
          `160 gold, +260 men. The Hosts +16, the Great Families −20. ` +
          `+0.16 unrest across ${plural(holdingsOf(c, k.nation), 'holding')}.`,
        ai: (c, k) => 0.3 + temper(c, k.nation).militarism * 0.8,
        apply: (c, k) => {
          giveManpower(c, k.nation, 260);
          nudgeEstate(c, k.nation, Estate.Hosts, 16);
          nudgeEstate(c, k.nation, Estate.Families, -20);
          addUnrest(c, heldBy(c, k.nation), 0.16);
          return `Two hundred and sixty more, taken off the ploughs in the wrong month, marched off to a war they will have to have explained to them.`;
        },
      },
      {
        id: 'home_for_harvest',
        label: 'Let the men go home for the harvest.',
        rules: (c, k) =>
          `Every host of ours loses a twelfth of its models. The Great Families +22, the Hosts −16. ` +
          `−0.14 unrest across ${plural(holdingsOf(c, k.nation), 'holding')}.`,
        ai: (c, k) => 0.35 + temper(c, k.nation).caution * 0.6,
        apply: (c, k) => {
          let gone = 0;
          // Over a copy: a one-model host goes home for the harvest in its
          // entirety, and burying it mutates the list being walked.
          for (const a of [...armiesOf(c, k.nation)]) {
            gone += applyLosses(a, 1 - 1 / 12);
            if (a.regiments.length === 0) removeArmy(c, a.id);
          }
          addUnrest(c, heldBy(c, k.nation), -0.14);
          nudgeEstate(c, k.nation, Estate.Families, 22);
          nudgeEstate(c, k.nation, Estate.Hosts, -16);
          return `${plural(gone, 'man')} went home for six weeks. Most of them came back, and the fields were got in, and the war did not notice either way.`;
        },
      },
      {
        id: 'preach_it',
        label: 'Preach it. Make it about something.',
        rules: (c, k) =>
          sameConfession(c, k.nation, k.other)
            ? `The Temple −24 — they pray as we do. The Hosts +14. Every crown of our confession thinks −6 of us.`
            : `The Temple +26 — they do not pray as we do. The Hosts +14. Every crown of our confession thinks +6 of us.`,
        ai: (c, k) => (sameConfession(c, k.nation, k.other) ? 0.15 : 0.5),
        apply: (c, k) => {
          const same = sameConfession(c, k.nation, k.other);
          nudgeEstate(c, k.nation, Estate.Temple, same ? -24 : 26);
          nudgeEstate(c, k.nation, Estate.Hosts, 14);
          for (const o of others(c, k.nation)) {
            if (o !== k.other && sameConfession(c, o, k.nation)) {
              addOpinion(c, o, k.nation, same ? -6 : 6);
            }
          }
          return same
            ? `It was preached, and it was noticed that the men on the other side of the field were being preached the same sermon.`
            : `It was preached in every house of the confession, and the war has a name now, and the name is doing the recruiting.`;
        },
      },
    ],
  },

  // ── Trade ────────────────────────────────────────────────────────────────

  {
    id: 'plague_on_the_lane',
    kind: 'trade',
    title: 'Something Came Up The Trade Road',
    summary: 'It arrived with the carts, and the carts came from a long way off.',
    weight: 5,
    repeat: 3,
    when: (c, n) => {
      // Measured: seventeen of twenty-four trade nodes have a dominant power, and
      // the idle player holds control in exactly one — their own. So this is
      // gated on holding a *stake* rather than on dominating anything.
      const held = heldBy(c, n);
      if (held.length === 0) return null;
      let best = -1;
      let bestShare = 0.12;
      for (const p of held) {
        const node = tradeNodeOf(c, p);
        if (node < 0) continue;
        const share = controlIn(c, node, n);
        if (share > bestShare) {
          bestShare = share;
          best = node;
        }
      }
      if (best < 0) return null;
      // The provinces of that node we actually hold. That is what gets sick.
      const node = tradeNode(c, best);
      if (!node) return null;
      const ours = node.provinces.filter((p) => c.provinces[p].owner === n);
      if (ours.length === 0) return null;
      return ctx(n, { province: ours[0], region: regionOf(ours[0]) });
    },
    flavour: (c, k) => {
      const node = tradeNodeOf(c, k.province);
      return (
        `It came up the road into ${nodeName(c, node)} with a train of carts that had been four ` +
        `months on the water and the road, and it was in ${provinceName(k.province)} before the ` +
        `carts were unloaded. We hold ${Math.round(controlIn(c, node, k.nation) * 100)} per cent of ` +
        `that market. That is the reason it is our problem and not somebody else’s.`
      );
    },
    choices: [
      {
        id: 'shut_the_road',
        label: 'Shut the road. All of it. Today.',
        rules: (c, k) => {
          const node = tradeNodeOf(c, k.province);
          return (
            `The Counting Houses −26, the Temple +12. −0.1 unrest in the provinces of ` +
            `${nodeName(c, node)} that we hold. Nobody dies.`
          );
        },
        ai: (c, k) => 0.35 + temper(c, k.nation).caution * 0.7,
        apply: (c, k) => {
          const node = tradeNodeOf(c, k.province);
          const def = tradeNode(c, node);
          const ours = (def?.provinces ?? []).filter((p) => c.provinces[p].owner === k.nation);
          addUnrest(c, ours, -0.1);
          nudgeEstate(c, k.nation, Estate.Merchants, -26);
          nudgeEstate(c, k.nation, Estate.Temple, 12);
          return `Barriers on every road into ${nodeName(c, node)} for eleven weeks. It worked, and it will be brought up at every meeting for a decade.`;
        },
      },
      {
        id: 'burn_the_carts',
        label: 'Burn the carts that came in and keep the road open.',
        cost: () => ({ gold: 110 }),
        rules: (c, k) => {
          const node = tradeNodeOf(c, k.province);
          const def = tradeNode(c, node);
          const ours = (def?.provinces ?? []).filter((p) => c.provinces[p].owner === k.nation);
          return `110 gold in compensation. The Counting Houses +8. +0.14 unrest across ${plural(ours.length, 'holding')} in ${nodeName(c, node)}.`;
        },
        // The middle course, and it has to be *wanted* rather than merely
        // available: at a flat 0.45 against two free choices it lost every ballot
        // in three hundred firings, which is the definition of a face nobody sees.
        ai: (c, k) => 0.8 - Math.abs(temper(c, k.nation).caution - 0.55),
        apply: (c, k) => {
          const node = tradeNodeOf(c, k.province);
          const def = tradeNode(c, node);
          const ours = (def?.provinces ?? []).filter((p) => c.provinces[p].owner === k.nation);
          addUnrest(c, ours, 0.14);
          nudgeEstate(c, k.nation, Estate.Merchants, 8);
          return `Forty carts and everything on them, burned on the road outside ${provinceName(k.province)}, and the owners paid for out of the treasury.`;
        },
      },
      {
        id: 'say_nothing',
        label: 'Keep the road open. Say nothing.',
        rules: (c, k) => {
          const node = tradeNodeOf(c, k.province);
          const def = tradeNode(c, node);
          const ours = (def?.provinces ?? []).filter((p) => c.provinces[p].owner === k.nation);
          return (
            `The Counting Houses +22, the Temple −18, the Great Families −12. ` +
            `+0.3 unrest and a fifth off the garrison across ${plural(ours.length, 'holding')} in ${nodeName(c, node)}.`
          );
        },
        /**
         * Wanted by a crown that lives off the wharf, and by a reckless one.
         * At `0.2 + merchants/200` it was taken **zero** times in three hundred
         * and nine firings — the counting houses are usually in deficit, so the
         * term it keyed off was almost always negative and `shut_the_road` won
         * every single time. A card whose third face never turns over is a card
         * with two faces.
         */
        ai: (c, k) =>
          0.45 +
          Math.max(0, standing(c, k.nation, Estate.Merchants)) / 150 +
          (1 - temper(c, k.nation).caution) * 0.4,
        apply: (c, k) => {
          const node = tradeNodeOf(c, k.province);
          const def = tradeNode(c, node);
          const ours = (def?.provinces ?? []).filter((p) => c.provinces[p].owner === k.nation);
          addUnrest(c, ours, 0.3);
          for (const p of ours) c.provinces[p].garrison = Math.max(0, c.provinces[p].garrison * 0.8);
          nudgeEstate(c, k.nation, Estate.Merchants, 22);
          nudgeEstate(c, k.nation, Estate.Temple, -18);
          nudgeEstate(c, k.nation, Estate.Families, -12);
          return `The market at ${nodeName(c, node)} did not close for a single day, and the watch-lists were short all winter.`;
        },
      },
    ],
  },

  {
    id: 'a_town_that_wants_out',
    kind: 'country',
    title: 'A Holding That Would Rather Not',
    summary: 'One of ours has stopped pretending.',
    weight: 5,
    repeat: 4,
    when: (c, n) => {
      // Measured: seven provinces on the board sit above 0.4 unrest in a typical
      // month and ten above 0.2, so a crown with a genuinely restive holding is
      // a real and recurring position.
      let worst = -1;
      for (let p = 0; p < c.provinces.length; p++) {
        const prov = c.provinces[p];
        if (prov.owner !== n || prov.unrest < 0.45) continue;
        if (worst < 0 || prov.unrest > c.provinces[worst].unrest) worst = p;
      }
      if (worst < 0) return null;
      return ctx(n, { province: worst, region: regionOf(worst) });
    },
    flavour: (c, k) =>
      `${provinceName(k.province)} has not sent its reckoning, has not sent its levy, and has sent ` +
      `back the man who went to ask about either. Unrest there stands at ` +
      `${Math.round(c.provinces[k.province].unrest * 100)} in a hundred. It is ` +
      `${groundOf(k.region)} ground and it was ${c.provinces[k.province].claimedBy >= 0 ? 'somebody else’s within living memory' : 'always ours, which is what makes this awkward'}.`,
    choices: [
      {
        id: 'remit',
        label: 'Remit the year’s dues and put it in writing.',
        cost: () => ({ gold: 130 }),
        rules: (_c, k) =>
          `130 gold. −0.35 unrest at ${provinceName(k.province)}. The Great Families +12, the Counting Houses −14.`,
        ai: (c, k) => 0.4 + temper(c, k.nation).caution * 0.5,
        apply: (c, k) => {
          addUnrest(c, [k.province], -0.35);
          nudgeEstate(c, k.nation, Estate.Families, 12);
          nudgeEstate(c, k.nation, Estate.Merchants, -14);
          return `A year forgiven, read out in the square at ${provinceName(k.province)} by a man who had to be given an escort to get there and did not need it to get back.`;
        },
      },
      {
        id: 'garrison_hard',
        label: 'Put the garrison in the square and leave it there.',
        rules: (_c, k) =>
          `Costs 90 men. ${provinceName(k.province)} goes to its garrison cap and −0.2 unrest. ` +
          `The Hosts +14, the Temple −14, the Great Families −10.`,
        ai: (c, k) => 0.3 + temper(c, k.nation).aggression * 0.8,
        apply: (c, k) => {
          takeManpower(c, k.nation, 90);
          c.provinces[k.province].garrison = garrisonCap(c, k.province);
          addUnrest(c, [k.province], -0.2);
          nudgeEstate(c, k.nation, Estate.Hosts, 14);
          nudgeEstate(c, k.nation, Estate.Temple, -14);
          nudgeEstate(c, k.nation, Estate.Families, -10);
          return `They are still in the square at ${provinceName(k.province)}. The dues came in on time and were counted out in front of them.`;
        },
      },
      {
        id: 'let_it_go_bad',
        label: 'Leave it. It will burn itself out or it will not.',
        rules: (_c, k) =>
          `A gamble, decided now: either ${provinceName(k.province)} settles by ${Math.round(CONQUEST_UNREST * 100)} in a hundred, ` +
          `or brigands take the hills above it. The Great Families −16.`,
        // Reckless crowns gamble, and a crown with nobody to send has to. Both,
        // or `garrison_hard` wins every time a purse is empty — which is every
        // time this card comes up.
        ai: (c, k) =>
          0.45 +
          (1 - temper(c, k.nation).caution) * 0.5 +
          (c.nations[k.nation].manpower < 120 ? 0.4 : 0),
        apply: (c, k, rng) => {
          nudgeEstate(c, k.nation, Estate.Families, -16);
          // Drawn from the campaign stream, whose cursor is hashed, so a realm
          // where this came up the other way is a visibly different realm.
          if (chance(rng, 0.5)) {
            addUnrest(c, [k.province], -CONQUEST_UNREST);
            return `It burned itself out over a winter. Nobody has explained why and nobody has asked in writing.`;
          }
          if (!campAt(c, k.province)) {
            c.camps.push({
              at: k.province,
              strength: 110 + nextInt(rng, 0, 40),
              founded: c.turn,
              bounty: 0,
            });
            c.camps.sort((a, b) => a.at - b.at);
          }
          addUnrest(c, [k.province], 0.1);
          return `It did not burn itself out. There are men in the hills above ${provinceName(k.province)} now, and some of them used to be the watch.`;
        },
      },
    ],
  },
];

/** One card by id, or undefined. The deck is small enough that a walk is right. */
export function realmEvent(id: string): RealmEventDef | undefined {
  return REALM_EVENTS.find((e) => e.id === id);
}

/**
 * Everything this crown could be asked this month, with the context each one
 * would fire about.
 *
 * Pure: no random numbers, no writes. `realmEventTurn` draws from what this
 * returns, and the tests call it directly to count how often the board actually
 * produces each card — which is the check this project has twice shipped without.
 */
export function eligibleRealmEvents(
  c: CampaignState,
  nation: number,
  seen: (id: string) => number,
): Array<{ def: RealmEventDef; k: RealmEventCtx }> {
  const out: Array<{ def: RealmEventDef; k: RealmEventCtx }> = [];
  for (const def of REALM_EVENTS) {
    if (seen(def.id) >= (def.repeat ?? 1)) continue;
    const k = def.when(c, nation);
    if (!k) continue;
    out.push({ def, k });
  }
  return out;
}
